import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResultBadge, inr } from "./ResultBadge";
import { StatusPill } from "@/components/app/Primitives";
import { Compass } from "lucide-react";

const HOME = { ey_auditor: "/ey", procurement_desk: "/procurement", customer: "/customer", propay_admin: "/admin" };

export function ScenarioSwitcher({ current }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/demo/scenarios").then((r) => setList(r.data)).catch(() => {}); }, []);
  if (!list.length) return null;
  const home = HOME[user?.role] || "/admin";
  return (
    <div className="flex items-center gap-2" data-testid="scenario-switcher">
      <Compass size={15} className="text-amber-600" />
      <Select value={current || ""} onValueChange={(cid) => { const s = list.find((x) => x.campaign_id === cid); nav(`${home}/campaigns/${cid}?tab=${s?.tab || "overview"}`); }}>
        <SelectTrigger className="h-9 w-[300px] bg-amber-50 border-amber-200 text-amber-900 text-xs" data-testid="scenario-select"><SelectValue placeholder="Jump to demo scenario…" /></SelectTrigger>
        <SelectContent className="max-h-[420px]">
          {list.map((s) => (
            <SelectItem key={s.campaign_id} value={s.campaign_id} data-testid={`scenario-option-${s.scenario}`}>
              <div className="flex flex-col"><span className="font-semibold text-xs">{s.label} <span className="text-slate-400 font-normal">· {s.name}</span></span><span className="text-[10px] text-slate-500">{s.expected}</span></div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function ReconciliationPanel({ cid }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/campaigns/${cid}/reconciliation`).then((r) => setData(r.data)); }, [cid]);
  if (!data) return <div className="text-sm text-slate-400">Loading reconciliation…</div>;
  const t = data.totals;
  return (
    <div className="space-y-4" data-testid="reconciliation-panel">
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 rounded-xl border border-slate-200 bg-white p-5">
        {[["Approved plan", t.approved, "approved"], ["PO allocation", t.po_allocation, "po-allocation"], ["Invoiced", t.invoiced, "invoiced"], ["Cleared", t.cleared, "cleared"], ["Paid", t.paid, "paid"], ["Variance", t.variance, "variance"]].map(([l, v, k]) => (
          <div key={l} data-testid={`recon-total-${k}`}><div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div><div className={`font-mono text-lg font-bold ${l === "Variance" && v !== 0 ? "text-rose-600" : "text-slate-900"}`}>{inr(v)}</div></div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-3 py-2">Creator</th><th className="px-3 py-2">KYS</th><th className="px-3 py-2 text-right">Approved</th><th className="px-3 py-2 text-right">PO alloc.</th><th className="px-3 py-2">Deliverables</th><th className="px-3 py-2">PoE</th><th className="px-3 py-2">AI</th><th className="px-3 py-2 text-right">Invoiced</th><th className="px-3 py-2 text-right">Paid</th><th className="px-3 py-2 text-right">Variance</th><th className="px-3 py-2">Rules</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Explanation</th></tr>
          </thead>
          <tbody>
            {data.lines.map((l) => (
              <tr key={l.order_id} className="border-t border-slate-100 align-top" data-testid={`recon-line-${l.order_id}`}>
                <td className="px-3 py-2"><div className="font-semibold text-slate-800">{l.creator}</div><div className="font-mono text-[11px] text-slate-400">{l.handle} · {l.order_number}</div></td>
                <td className="px-3 py-2"><StatusPill status={l.kys_status} /></td>
                <td className="px-3 py-2 text-right font-mono">{inr(l.approved)}</td>
                <td className="px-3 py-2 text-right font-mono text-slate-500">{inr(l.po_allocation)}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.deliverables_actual}/{l.deliverables_planned}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.poe_complete}/{l.deliverables_actual}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.ai_pass}/{l.ai_reviewed}</td>
                <td className="px-3 py-2 text-right font-mono">{inr(l.invoiced)}<div className="text-[10px] text-slate-400">{l.invoice_count} inv</div></td>
                <td className="px-3 py-2 text-right font-mono">{inr(l.paid)}</td>
                <td className={`px-3 py-2 text-right font-mono ${l.variance ? "text-rose-600" : "text-slate-500"}`}>{l.variance ? inr(l.variance) : "—"}</td>
                <td className="px-3 py-2 text-xs"><span className="text-rose-600 font-mono">{l.rule_fails}F</span> <span className="text-amber-600 font-mono">{l.rule_reviews}R</span></td>
                <td className="px-3 py-2"><ResultBadge value={l.status === "audit_clear" ? "PASS" : l.status === "blocked" ? "FAIL" : "NEEDS_REVIEW"} /></td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-[240px]">{l.explanation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-400">Generated {new Date(data.generated_at).toLocaleString()} · audit run {data.audit_run ? new Date(data.audit_run.executed_at).toLocaleString() : "not yet executed"} · "Cleared" populates once Audit Clearance (Stage G) is granted.</div>
    </div>
  );
}
